import { useEffect, useMemo, useRef, useState } from "react";
import type {
  PowerGenerationPayload,
  PowerGenLatestKey,
  PowerGenSeries,
  PowerGenSeriesKey,
} from "../lib/api";
import { ChartCard } from "./ChartCard";
import { ChartLegend } from "./ChartLegend";
import { LineChart } from "./LineChart";
import type { Series } from "../lib/chart";

interface Props {
  data: PowerGenerationPayload;
}

const COLORS: Record<PowerGenSeriesKey, string> = {
  wind: "#38bdf8", // sky-400
  solar: "#fbbf24", // amber-400
};

const SERIES_LABELS: Record<PowerGenSeriesKey, string> = {
  wind: "Wind",
  solar: "Solar",
};

const LATEST_LABELS: Record<PowerGenLatestKey, string> = {
  windOnshore: "Wind Onshore",
  windOffshore: "Wind Offshore",
  solar: "Solar",
};

const LATEST_COLORS: Record<PowerGenLatestKey, string> = {
  windOnshore: "#38bdf8", // sky-400
  windOffshore: "#22d3ee", // cyan-400
  solar: "#fbbf24", // amber-400
};

const LATEST_KEYS: PowerGenLatestKey[] = ["windOnshore", "windOffshore", "solar"];
const SERIES_KEYS: PowerGenSeriesKey[] = ["wind", "solar"];

/**
 * 7-day realized + 7-day weather-driven forecast for German renewables.
 * Chart aggregates to two sources (Wind, Solar) so realized and forecast
 * match in granularity — EWC publishes proper 7-day forecasts only at the
 * DE-aggregated level. The headline keeps the finer Wind Onshore /
 * Wind Offshore / Solar breakdown because those numbers are still
 * informative.
 *
 * Realized lags by 1–2 days; forecast picks up at "today" and extends a
 * week, giving the dashed forecast line a natural continuation point
 * marked by the "Jetzt"-line.
 */
export function PowerGenerationChart({ data }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(720);
  const height = 320;

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      setWidth(Math.max(320, Math.floor(entry.contentRect.width)));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const [mode, setMode] = useState<"lines" | "stacked">("lines");
  const [visible, setVisible] = useState<Record<PowerGenSeriesKey, boolean>>({
    wind: true,
    solar: true,
  });

  const seriesBySourceKind = useMemo(() => {
    const m = new Map<string, PowerGenSeries>();
    for (const s of data.series) m.set(`${s.source}-${s.kind}`, s);
    return m;
  }, [data.series]);

  const lineSeries: Series[] = useMemo(() => {
    const out: Series[] = [];
    for (const src of SERIES_KEYS) {
      if (!visible[src]) continue;
      const rea = seriesBySourceKind.get(`${src}-realized`);
      const fc = seriesBySourceKind.get(`${src}-forecast`);
      if (rea && rea.points.length > 0) {
        out.push({
          id: `${src}-rea`,
          label: `${SERIES_LABELS[src]} Ist`,
          color: COLORS[src],
          points: rea.points.map((p) => ({ x: p.ts, y: p.value })),
        });
      }
      if (fc && fc.points.length > 0) {
        out.push({
          id: `${src}-fc`,
          label: `${SERIES_LABELS[src]} Forecast`,
          color: COLORS[src],
          dashed: true,
          points: fc.points.map((p) => ({ x: p.ts, y: p.value })),
        });
      }
    }
    return out;
  }, [visible, seriesBySourceKind]);

  const stackedBands = useMemo(() => buildStackedBands(data.series, visible), [data.series, visible]);

  const headlineTotal =
    (data.latest.windOnshore?.value ?? 0) +
    (data.latest.windOffshore?.value ?? 0) +
    (data.latest.solar?.value ?? 0);

  const toggle = (src: PowerGenSeriesKey) =>
    setVisible((prev) => ({ ...prev, [src]: !prev[src] }));

  return (
    <ChartCard
      title="Erzeugung Wind & Solar"
      unit="MW"
      accent="sky"
      updatedAt={data.updatedAt}
      footer={
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <ChartLegend
            entries={SERIES_KEYS.map((src) => ({
              id: src,
              label: SERIES_LABELS[src],
              color: COLORS[src],
              visible: visible[src],
            }))}
            onToggle={(id) => toggle(id as PowerGenSeriesKey)}
          />
          <div className="inline-flex rounded-md bg-slate-100 dark:bg-slate-800 text-xs">
            <ModeBtn label="Linien" active={mode === "lines"} onClick={() => setMode("lines")} />
            <ModeBtn label="Stacked" active={mode === "stacked"} onClick={() => setMode("stacked")} />
          </div>
        </div>
      }
    >
      {hasAnyError(data) && (
        <div className="mb-2 text-[11px] text-rose-700 dark:text-rose-300">
          Teilweise unvollständige Daten:{" "}
          {Object.entries(data.errors)
            .map(([k, v]) => `${k}: ${v}`)
            .join(" · ")}
        </div>
      )}

      <div className="grid grid-cols-[repeat(4,minmax(0,1fr))] gap-3 mb-3">
        {LATEST_KEYS.map((src) => (
          <SummaryBlock
            key={src}
            label={LATEST_LABELS[src]}
            value={data.latest[src]?.value ?? null}
            ts={data.latest[src]?.ts ?? null}
            color={LATEST_COLORS[src]}
          />
        ))}
        <SummaryBlock
          label="Σ Erneuerbar"
          value={Object.keys(data.latest).length > 0 ? headlineTotal : null}
          ts={null}
          color="#a3e635"
        />
      </div>

      <div ref={containerRef}>
        {mode === "lines" ? (
          <LineChart
            series={lineSeries}
            unit="MW"
            nowMarker={data.now}
            height={height}
            formatValue={fmtAxisMw}
          />
        ) : (
          <StackedAreaChart bands={stackedBands} width={width} height={height} now={data.now} />
        )}
      </div>
    </ChartCard>
  );
}

function ModeBtn({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "px-2.5 py-1 rounded-md font-medium transition " +
        (active
          ? "bg-white dark:bg-slate-950 text-slate-900 dark:text-white shadow-sm ring-1 ring-slate-200 dark:ring-slate-700"
          : "text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100")
      }
    >
      {label}
    </button>
  );
}

function SummaryBlock({
  label,
  value,
  ts,
  color,
}: {
  label: string;
  value: number | null;
  ts: string | null;
  color: string;
}) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400 inline-flex items-center gap-1.5">
        <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: color }} />
        {label}
      </div>
      <div className="font-semibold tabular-nums text-lg sm:text-xl" style={{ color }}>
        {value === null ? "—" : fmtMw(value)}
      </div>
      <div className="text-[11px] text-slate-500 dark:text-slate-400">
        {ts ? fmtTsShort(ts) : "—"}
      </div>
    </div>
  );
}

const AXIS_MW_FMT = new Intl.NumberFormat("de-DE", { maximumFractionDigits: 0 });

/**
 * Y-axis / tooltip formatter for the line chart: integer MW with thousands
 * separators (e.g. "21.000"), no decimals. Keeps the unit consistent with the
 * "MW" axis label and — unlike the default 2-decimal price formatter — stays
 * narrow enough to fit the chart's left gutter instead of being clipped.
 */
function fmtAxisMw(mw: number): string {
  return AXIS_MW_FMT.format(mw);
}

function fmtMw(mw: number): string {
  if (Math.abs(mw) >= 10_000) {
    return `${(mw / 1000).toLocaleString("de-DE", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} GW`;
  }
  return `${Math.round(mw).toLocaleString("de-DE")} MW`;
}

function fmtTsShort(ts: string): string {
  const t = Date.parse(ts);
  if (!Number.isFinite(t)) return ts;
  return new Date(t).toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Berlin",
  });
}

function hasAnyError(d: PowerGenerationPayload): boolean {
  return Object.keys(d.errors).length > 0;
}

// ---- Stacked-area mode ----------------------------------------------------

interface StackedSlot {
  ts: string;
  ms: number;
  wind: number | null;
  solar: number | null;
}

function buildStackedBands(
  allSeries: readonly PowerGenSeries[],
  visible: Record<PowerGenSeriesKey, boolean>,
): StackedSlot[] {
  // Realized wins where realized + forecast both exist for the same ts.
  const perSource: Record<PowerGenSeriesKey, Map<string, number | null>> = {
    wind: new Map(),
    solar: new Map(),
  };
  for (const s of allSeries) {
    if (!visible[s.source]) continue;
    for (const p of s.points) {
      if (s.kind === "realized") {
        perSource[s.source].set(p.ts, p.value);
      } else if (!perSource[s.source].has(p.ts)) {
        perSource[s.source].set(p.ts, p.value);
      }
    }
  }
  const allTs = new Set<string>();
  for (const src of SERIES_KEYS) for (const ts of perSource[src].keys()) allTs.add(ts);
  const slots: StackedSlot[] = [...allTs]
    .map((ts) => ({
      ts,
      ms: Date.parse(ts),
      wind: perSource.wind.get(ts) ?? null,
      solar: perSource.solar.get(ts) ?? null,
    }))
    .filter((s) => Number.isFinite(s.ms))
    .sort((a, b) => a.ms - b.ms);
  return slots;
}

function StackedAreaChart({
  bands,
  width,
  height,
  now,
}: {
  bands: StackedSlot[];
  width: number;
  height: number;
  now: string;
}) {
  const padding = { top: 14, right: 16, bottom: 28, left: 56 };
  const innerW = Math.max(0, width - padding.left - padding.right);
  const innerH = Math.max(0, height - padding.top - padding.bottom);

  const xMin = bands[0]?.ms ?? 0;
  const xMax = bands[bands.length - 1]?.ms ?? xMin + 1;
  let yMax = 0;
  for (const s of bands) {
    const total = (s.wind ?? 0) + (s.solar ?? 0);
    if (total > yMax) yMax = total;
  }
  if (yMax === 0) yMax = 1;
  const yPad = yMax * 0.05;
  yMax += yPad;

  const xScale = (ms: number) => padding.left + ((ms - xMin) / (xMax - xMin || 1)) * innerW;
  const yScale = (v: number) => padding.top + (1 - v / yMax) * innerH;

  const layers: Array<{ source: PowerGenSeriesKey; path: string }> = [];
  let cumPrev: number[] = bands.map(() => 0);
  for (const src of SERIES_KEYS) {
    const cumNext: number[] = bands.map((s, i) => cumPrev[i] + (s[src] ?? 0));
    const top = bands
      .map((s, i) => `${xScale(s.ms).toFixed(1)} ${yScale(cumNext[i]).toFixed(1)}`)
      .join(" L ");
    const bottomRev = bands
      .slice()
      .reverse()
      .map((s, i) => {
        const idx = bands.length - 1 - i;
        return `${xScale(s.ms).toFixed(1)} ${yScale(cumPrev[idx]).toFixed(1)}`;
      })
      .join(" L ");
    layers.push({
      source: src,
      path: bands.length > 0 ? `M ${top} L ${bottomRev} Z` : "",
    });
    cumPrev = cumNext;
  }

  const yTicks = niceYTicks(yMax, 4);
  const xTicks = niceXTicks(xMin, xMax, 6);

  const nowMs = Date.parse(now);
  const showNow = Number.isFinite(nowMs) && nowMs >= xMin && nowMs <= xMax;

  if (bands.length === 0) {
    return (
      <div
        className="flex items-center justify-center text-xs text-slate-500 dark:text-slate-400"
        style={{ height }}
      >
        Keine Daten in der aktuellen Auswahl.
      </div>
    );
  }

  return (
    <svg width={width} height={height} role="img" aria-label="Erzeugungs-Stack">
      {yTicks.map((v, i) => (
        <g key={i}>
          <line
            x1={padding.left}
            x2={padding.left + innerW}
            y1={yScale(v)}
            y2={yScale(v)}
            stroke="currentColor"
            className="text-slate-200/60 dark:text-slate-700/40"
            strokeWidth={1}
          />
          <text
            x={padding.left - 6}
            y={yScale(v)}
            textAnchor="end"
            dominantBaseline="middle"
            className="fill-slate-500 dark:fill-slate-400"
            style={{ fontSize: 10 }}
          >
            {fmtMw(v)}
          </text>
        </g>
      ))}
      {xTicks.map((t, i) => (
        <g key={i}>
          <line
            x1={xScale(t)}
            x2={xScale(t)}
            y1={padding.top + innerH}
            y2={padding.top + innerH + 4}
            stroke="currentColor"
            className="text-slate-400 dark:text-slate-500"
            strokeWidth={1}
          />
          <text
            x={xScale(t)}
            y={padding.top + innerH + 16}
            textAnchor="middle"
            style={{ fontSize: 10 }}
            className="fill-slate-500 dark:fill-slate-400"
          >
            {fmtDayLabel(t)}
          </text>
        </g>
      ))}
      {layers.map((l) => (
        <path
          key={l.source}
          d={l.path}
          fill={COLORS[l.source]}
          fillOpacity={0.55}
          stroke={COLORS[l.source]}
          strokeWidth={0.5}
        />
      ))}
      {showNow && (
        <line
          x1={xScale(nowMs)}
          x2={xScale(nowMs)}
          y1={padding.top}
          y2={padding.top + innerH}
          stroke="currentColor"
          className="text-slate-400 dark:text-slate-500"
          strokeWidth={1}
          strokeDasharray="2 3"
        />
      )}
    </svg>
  );
}

function niceYTicks(yMax: number, count: number): number[] {
  const step = yMax / count;
  const out: number[] = [];
  for (let i = 0; i <= count; i++) out.push(step * i);
  return out;
}

function niceXTicks(xMin: number, xMax: number, count: number): number[] {
  const dayMs = 24 * 3600 * 1000;
  const span = xMax - xMin;
  const stepDays = Math.max(1, Math.round(span / dayMs / count));
  const out: number[] = [];
  let t = Math.ceil(xMin / dayMs) * dayMs;
  while (t <= xMax) {
    out.push(t);
    t += stepDays * dayMs;
  }
  return out;
}

function fmtDayLabel(ms: number): string {
  return new Date(ms).toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    timeZone: "Europe/Berlin",
  });
}
